import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { MapPin, ExternalLink, Loader2, TrendingUp, Users, MousePointerClick } from "lucide-react";
import { CITIES } from "../../data/cities";

/**
 * GeoAnalytics — dashboard for Geo-SEO city landing pages.
 *
 * Backend endpoint /admin/geo-analytics aggregates:
 *   - visits (from cms_geo_visits, logged by GeoLanding on mount)
 *   - leads  (from cms_leads where source starts with "geo:{slug}")
 *
 * Renders a ranked table so the team knows which cities to prioritize for
 * commercial follow-ups + which slugs to reinforce with paid campaigns.
 */
const DAY_OPTIONS = [7, 30, 90];
const GROUPS = [
  { key: "city", label: "Por cidade" },
  { key: "state", label: "Por UF / distrito" },
  { key: "country", label: "Por país" },
];

const COUNTRY_LABEL = { BR: "Brasil", PT: "Portugal", PY: "Paraguai" };

// Local lookup: enrich city_slug with human-friendly name + state (from static cities.js)
const CITY_INDEX = Object.fromEntries((CITIES || []).map((c) => [c.slug, c]));

/** Agrupa as linhas por UF ou país usando o índice estático de cidades.
 *  Cidades sem match caem em "—" para não sumirem do relatório. */
function groupRows(rows, mode) {
  if (mode === "city") return rows;
  const acc = {};
  for (const r of rows) {
    const city = CITY_INDEX[r.city_slug];
    const key = mode === "country" ? (city?.country || "—") : (city?.state || "—");
    const name = mode === "country"
      ? (COUNTRY_LABEL[key] || key)
      : (city?.state_name ? `${key} · ${city.state_name}` : key);
    if (!acc[key]) acc[key] = { key, name, visits: 0, leads: 0, cities: new Set(), last_visit: "" };
    acc[key].visits += r.visits || 0;
    acc[key].leads += r.leads || 0;
    acc[key].cities.add(r.city_slug);
    if ((r.last_visit || "") > acc[key].last_visit) acc[key].last_visit = r.last_visit || "";
  }
  return Object.values(acc)
    .map((g) => ({ ...g, cities_count: g.cities.size }))
    .sort((a, b) => b.visits - a.visits);
}

export default function GeoAnalytics() {
  const [days, setDays] = useState(30);
  const [group, setGroup] = useState("city");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/geo-analytics?days=${days}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [days]);

  const rawRows = data?.rows || [];
  const rows = groupRows(rawRows, group);
  const totals = data?.totals || {};
  const isCity = group === "city";

  return (
    <div data-testid="geo-analytics-page">
      <div className="flex items-center gap-3 mb-2">
        <MapPin className="w-6 h-6 text-accent-orange" />
        <h1 className="font-display text-4xl">Geo-SEO Analytics</h1>
      </div>
      <p className="text-sm text-neutral-500 mb-6 max-w-3xl">
        Cidades que mais recebem tráfego + leads nas landings Geo-SEO (URLs curtas <code>/cidade</code> e canônicas <code>/marketing-imobiliario-cidade</code>).
        Use pra priorizar follow-ups comerciais e reforço de mídia. Agrupe por UF ou país para enxergar tendências regionais.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="text-xs uppercase font-semibold tracking-widest text-neutral-500">Período:</span>
        {DAY_OPTIONS.map((d) => (
          <button key={d} onClick={() => setDays(d)}
            data-testid={`geo-range-${d}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full ${days === d ? "bg-black text-white" : "border border-black/15"}`}>
            Últimos {d} dias
          </button>
        ))}
        <span className="text-xs uppercase font-semibold tracking-widest text-neutral-500 ml-4">Agrupar:</span>
        {GROUPS.map((g) => (
          <button key={g.key} onClick={() => setGroup(g.key)}
            data-testid={`geo-group-${g.key}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full ${group === g.key ? "bg-accent-orange text-white" : "border border-black/15"}`}>
            {g.label}
          </button>
        ))}
      </div>

      {/* Totals cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card icon={<Users className="w-4 h-4" />} label="Visitas totais" value={totals.visits || 0} testid="geo-total-visits" />
        <Card icon={<MousePointerClick className="w-4 h-4" />} label="Leads gerados" value={totals.leads || 0} testid="geo-total-leads" />
        <Card icon={<TrendingUp className="w-4 h-4" />} label="Cidades com tráfego" value={totals.cities_with_traffic || 0} testid="geo-total-cities" />
      </div>

      {/* Ranking */}
      <div className="border border-black/10 rounded-md overflow-hidden">
        <div className="bg-neutral-50 border-b border-black/10 px-4 py-3">
          <div className="text-xs uppercase font-semibold tracking-widest text-neutral-700">
            {isCity ? "Ranking por cidade" : group === "state" ? "Ranking por UF / distrito" : "Ranking por país"}
          </div>
        </div>
        {loading ? (
          <div className="text-sm text-neutral-500 py-16 text-center flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-neutral-500 py-16 text-center">
            Nenhum tráfego registrado ainda. As páginas <code>/cidade</code> começam a coletar dados assim que forem visitadas.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-neutral-500 border-b border-black/10">
              <tr>
                <th className="text-left px-4 py-2 w-10">#</th>
                <th className="text-left px-4 py-2">{isCity ? "Cidade" : group === "state" ? "UF / Distrito" : "País"}</th>
                {!isCity && <th className="text-right px-4 py-2">Cidades</th>}
                <th className="text-right px-4 py-2">Visitas</th>
                <th className="text-right px-4 py-2">Leads</th>
                <th className="text-right px-4 py-2">Conv.</th>
                <th className="text-right px-4 py-2">Última visita</th>
                {isCity && <th className="text-right px-4 py-2 w-24">Abrir</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const city = isCity ? CITY_INDEX[r.city_slug] : null;
                const conv = r.visits ? ((r.leads / r.visits) * 100).toFixed(1) : "—";
                return (
                  <tr key={isCity ? r.city_slug : r.key} className="border-b border-black/5 hover:bg-neutral-50"
                    data-testid={isCity ? `geo-row-${r.city_slug}` : `geo-grouprow-${r.key}`}>
                    <td className="px-4 py-2.5 text-neutral-400 font-mono">{i + 1}</td>
                    <td className="px-4 py-2.5">
                      {isCity ? (
                        <>
                          <div className="font-semibold">{city?.name || r.city_slug}</div>
                          <div className="text-[11px] text-neutral-500 font-mono">{r.city_slug}{city?.state ? ` · ${city.state}` : ""}</div>
                        </>
                      ) : (
                        <div className="font-semibold">{r.name}</div>
                      )}
                    </td>
                    {!isCity && <td className="px-4 py-2.5 text-right font-mono text-neutral-500">{r.cities_count}</td>}
                    <td className="px-4 py-2.5 text-right font-mono">{r.visits}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.leads > 0 ? <span className="text-accent-orange font-bold">{r.leads}</span> : r.leads}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-neutral-500">{conv !== "—" ? `${conv}%` : "—"}</td>
                    <td className="px-4 py-2.5 text-right text-[11px] text-neutral-500 font-mono">{r.last_visit ? r.last_visit.slice(5, 16).replace("T", " ") : "—"}</td>
                    {isCity && (
                      <td className="px-4 py-2.5 text-right">
                        <a href={`/${r.city_slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-black">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Card({ icon, label, value, testid }) {
  return (
    <div className="border border-black/10 rounded-md p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase font-semibold tracking-widest text-neutral-500 mb-2">
        {icon} {label}
      </div>
      <div className="font-display text-4xl">{value.toLocaleString()}</div>
    </div>
  );
}
